// Read-only ARC contract interaction over MetaMask's injected provider.
// Uses raw eth_call only — no signing, no approvals, no transaction submission.
// Strict V1 rules: NO transfer permissions, NO custodial logic, NO fake balances.

import { hasMetaMask } from "./wallet";

// keccak256("balanceOf(address)").slice(0, 4 bytes)
const BALANCE_OF_SELECTOR = "0x70a08231";
const ZERO_ADDRESS = "0x0000000000000000000000000000000000000000";

/** Pads `address` to a 32-byte ABI-encoded calldata word. */
function encodeAddress(address) {
  return address.toLowerCase().replace(/^0x/, "").padStart(64, "0");
}

/**
 * Read ARC balance for `wallet` directly from the deployed contract.
 *
 * @returns {Promise<bigint>} balance in raw token units (18 decimals).
 *   Throws when:
 *     - MetaMask is not present
 *     - the contract address is the zero placeholder (pre-launch)
 *     - the wallet is on the wrong network and the call reverts
 */
export async function readArcBalance({ contractAddress, wallet }) {
  if (!hasMetaMask()) throw new Error("MetaMask not detected");
  if (!contractAddress || contractAddress.toLowerCase() === ZERO_ADDRESS)
    throw new Error("ARC contract is pre-launch — no balance to read yet");
  if (!wallet) throw new Error("Wallet address required");

  const data = BALANCE_OF_SELECTOR + encodeAddress(wallet);
  const result = await window.ethereum.request({
    method: "eth_call",
    params: [{ to: contractAddress, data }, "latest"],
  });

  if (!result || result === "0x") return 0n;
  return BigInt(result);
}

/**
 * Format a raw uint256 ARC balance into a human number (18 decimals).
 * Returns a plain JS number for display arithmetic — fine for tier checks
 * because the largest tier threshold is well within Number.MAX_SAFE_INTEGER.
 */
export function formatArcBalance(raw, decimals = 18) {
  if (raw == null) return 0;
  const divisor = 10n ** BigInt(decimals);
  const whole = raw / divisor;
  const frac = raw % divisor;
  // Two decimals of precision is enough for display.
  const fracStr = frac.toString().padStart(decimals, "0").slice(0, 2);
  return parseFloat(`${whole.toString()}.${fracStr}`);
}

/** Pure off-chain tier resolution. */
export function tierFromBalance(balance, tiers) {
  if (!tiers?.length) return null;
  let current = tiers[0];
  for (const t of tiers) {
    if (balance >= t.min_arc) current = t;
  }
  const idx = tiers.findIndex((t) => t.id === current.id);
  const next = tiers[idx + 1] || null;
  const progress = next
    ? Math.min(
        100,
        ((balance - current.min_arc) / (next.min_arc - current.min_arc)) * 100,
      )
    : 100;
  return { current, next, progress, index: idx };
}
